import { useEffect, useMemo, useState } from 'react';
import { BedDouble, Search, ShieldAlert, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { HotelVendorBadge } from '@/components/hotels/hotel-vendor-badge';
import { useAccess } from '@/lib/access';
import type { HotelOption, IPolicyOverride } from '@/lib/allocation/types';
import { MAX_REASON, overrideOf, ruleOf } from './out-of-policy';
import { useSearchHotels } from './use-allocation';
import { isPriced } from '@/lib/allocation/price';
import { Unpriced } from '@/components/allocation/unpriced';

interface HotelSearchDialogProps {
  caseUrn: string;
  /** The sub-case (PNR group) the reservation is booked for. */
  subCaseUrn?: string | undefined;
  airportCode: string;
  roomsRequired: number;
  /** BUG-RUN6-009: fallback pax count from the roster row for guest count. */
  paxCount?: number | undefined;
  trigger: React.ReactNode;
  /**
   * Place a soft-hold on an offer (step 1 of the 2-step flow). Resolves once
   * the HOLD card is live in the reservations panel; the dialog then closes.
   * `policyOverride` — an offer OUTSIDE the policy, chosen with a reason (it rides
   * on the hold to the book).
   */
  onHold: (
    offer: HotelOption,
    subCaseUrn: string,
    roomCount: number,
    policyOverride?: IPolicyOverride,
  ) => Promise<void>;
}

export function HotelSearchDialog({
  caseUrn,
  subCaseUrn,
  airportCode,
  roomsRequired,
  paxCount,
  trigger,
  onHold,
}: HotelSearchDialogProps) {
  const { t } = useTranslation('caseWork');
  const [open, setOpen] = useState(false);
  const [holding, setHolding] = useState(false);
  // Booking OUTSIDE the policy (PO 2026-10-27: «saltear la política si el usuario tiene
  // permisos»): a viewer holding `hotels.policy.override` also sees the offers the policy
  // rules out, each with the rules it breaks, and may hold one with a written reason. The
  // BFF asks booking for them only for that permission — never on the client's word.
  const mayOverride = useAccess().can('hotels.policy.override');
  const search = useSearchHotels(caseUrn, airportCode, undefined, subCaseUrn, paxCount, mayOverride);
  // Nearest first — the operator is placing stranded passengers, and "how far
  // from the airport" is the first question they triage by.
  const results = useMemo(
    () =>
      search.data
        ? search.data.filter((h) => h.policyCompliant).sort((a, b) => a.distanceKm - b.distanceKm)
        : undefined,
    [search.data],
  );
  // Outside the policy: booking's order (cheapest first), after the compliant ones.
  const outside = useMemo(() => (search.data ?? []).filter((h) => !h.policyCompliant), [search.data]);

  useEffect(() => {
    if (open) {
      search.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const hold = async (hotel: HotelOption, policyOverride?: IPolicyOverride) => {
    if (!subCaseUrn) return;
    setHolding(true);
    try {
      await onHold(hotel, subCaseUrn, Math.min(roomsRequired, hotel.availableRooms), policyOverride);
      setOpen(false);
    } finally {
      setHolding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <span
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
        role="button"
        tabIndex={0}
        className="inline-block"
      >
        {trigger}
      </span>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-4 w-4" aria-hidden />
            {t('hotelSearch.title', { airport: airportCode })}
          </DialogTitle>
          <DialogDescription>
            {t('hotelSearch.description', { rooms: roomsRequired })}
          </DialogDescription>
        </DialogHeader>

        {search.isError ? (
          <p role="alert" className="text-sm text-destructive">{t('assignment.searchFailed')}</p>
        ) : search.isPending || !results ? (
          <div className="space-y-2">
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : (
          <div className="max-h-[28rem] space-y-4 overflow-auto">
            {results.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="hotel-search-empty">
                {mayOverride
                  ? outside.length > 0
                    ? t('hotelSearch.emptyCompliant')
                    : t('hotelSearch.empty')
                  : t('hotelSearch.emptyAskSupervisor')}
              </p>
            ) : (
              <ul className="space-y-1" data-testid="hotel-search-results">
                {results.map((hotel) => (
                  <li
                    key={hotel.hotelUrn}
                    className="flex items-start gap-3 rounded border border-border p-3 text-sm"
                  >
                    <HotelFacts hotel={hotel} />
                    <Button
                      size="sm"
                      className="gap-1"
                      disabled={holding || !subCaseUrn}
                      title={!subCaseUrn ? t('hotelSearch.noSubCase') : t('hotelSearch.holdHint')}
                      onClick={() => void hold(hotel)}
                    >
                      <BedDouble className="h-3 w-3" aria-hidden />
                      {t('hotelSearch.holdCount', {
                        n: Math.min(roomsRequired, hotel.availableRooms),
                      })}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            {mayOverride && outside.length > 0 ? (
              <section className="space-y-2" data-testid="hotel-search-out-of-policy">
                <div>
                  <h3 className="flex items-center gap-1.5 text-sm font-semibold text-amber-800 dark:text-amber-200">
                    <ShieldAlert className="h-4 w-4" aria-hidden />
                    {t('hotelSearch.outOfPolicy.title', { count: outside.length })}
                  </h3>
                  <p className="text-xs text-muted-foreground">{t('hotelSearch.outOfPolicy.lead')}</p>
                </div>
                <ul className="space-y-1">
                  {outside.map((hotel) => (
                    <OutOfPolicyRow
                      key={hotel.offerUrn}
                      hotel={hotel}
                      rooms={Math.min(roomsRequired, hotel.availableRooms)}
                      disabled={holding || !subCaseUrn}
                      onHold={(override) => hold(hotel, override)}
                    />
                  ))}
                </ul>
              </section>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Name, stars, vendor, distance, rooms and the nightly rate — every row alike. */
function HotelFacts({ hotel }: { hotel: HotelOption }) {
  const { t } = useTranslation('caseWork');
  return (
    <>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 font-medium">
          <span>{hotel.name}</span>
          <span className="flex items-center text-amber-600">
            {Array.from({ length: hotel.stars }).map((_, i) => (
              <Star key={i} className="h-3 w-3 fill-current" aria-hidden />
            ))}
          </span>
          <HotelVendorBadge urn={hotel.hotelUrn} />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('hotelSearch.meta', {
            km: hotel.distanceKm.toFixed(1),
            rooms: hotel.availableRooms,
          })}
        </p>
      </div>
      <div className="w-20 shrink-0 text-right">
        <p className="text-base font-semibold tabular-nums">
          {isPriced(hotel.ratePerRoomUSD) ? `$${hotel.ratePerRoomUSD}` : <Unpriced className="text-xs" />}
        </p>
        <p className="text-[11px] leading-tight text-muted-foreground">
          {t('common:holds.perNight')}
        </p>
      </div>
    </>
  );
}

/**
 * An offer the policy rules out: the rules it breaks, in words, and «Elegir igual…» —
 * which asks WHY before anything is held (booking records the reason, who and when).
 */
function OutOfPolicyRow({
  hotel,
  rooms,
  disabled,
  onHold,
}: {
  hotel: HotelOption;
  rooms: number;
  disabled: boolean;
  onHold: (override: IPolicyOverride) => Promise<void>;
}) {
  const { t } = useTranslation('caseWork');
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  const override = overrideOf(hotel, reason);
  return (
    <li
      className="space-y-2 rounded border border-amber-700/40 bg-amber-50 p-3 text-sm dark:bg-amber-900/20"
      data-testid={`out-of-policy-${hotel.hotelUrn}`}
    >
      <div className="flex items-start gap-3">
        <HotelFacts hotel={hotel} />
        {asking ? null : (
          <Button
            size="sm"
            variant="outline"
            className="border-amber-700/40"
            disabled={disabled}
            onClick={() => setAsking(true)}
            data-testid="out-of-policy-choose"
          >
            {t('hotelSearch.outOfPolicy.choose')}
          </Button>
        )}
      </div>
      <ul className="space-y-0.5 text-xs text-amber-800 dark:text-amber-200" data-testid="out-of-policy-why">
        {(hotel.violations ?? []).map((v, i) => {
          const rule = ruleOf(hotel, v);
          return <li key={`${v.code}-${i}`}>{t(`hotelSearch.outOfPolicy.why.${rule.key}`, rule.vars)}</li>;
        })}
      </ul>
      {asking ? (
        <div className="space-y-2">
          <label className="block text-xs font-medium">
            {t('hotelSearch.outOfPolicy.reasonLabel')}
            <textarea
              value={reason}
              maxLength={MAX_REASON}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder={t('hotelSearch.outOfPolicy.reasonPlaceholder')}
              className="mt-1 w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm font-normal"
              data-testid="out-of-policy-reason"
            />
          </label>
          <p className="text-[11px] text-muted-foreground">{t('hotelSearch.outOfPolicy.record')}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              className="gap-1"
              disabled={disabled || !override}
              onClick={() => {
                if (override) void onHold(override);
              }}
              data-testid="out-of-policy-hold"
            >
              <BedDouble className="h-3 w-3" aria-hidden />
              {t('hotelSearch.outOfPolicy.hold', { n: rooms })}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setAsking(false);
                setReason('');
              }}
            >
              {t('hotelSearch.outOfPolicy.cancel')}
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}
